import React, { useState, useRef, useEffect } from "react";
import classNames from "classnames";
import { useOnClickOutside } from "../../../../hooks/useOnClickOutside";
import { ResendOTPSMSBayi } from "../../../../services/authService";

function SmsVerification({
  shouldShow,
  updateModalState,
  handleAfterClose,
  duration,
  setDuration,
  handleAfterSubmit,
  code,
  setCode,
  jwt,
  setJwt,
  successMsg,
  setSuccessMsg,
  errMsg,
  setErrMsg,
  isLoading,
  otpLength = 6,
  closeOnOutsideClick = true,
  title,
  description,
  showResend = true,
}) {
  const [digits, setDigits] = useState(() => Array(otpLength).fill(""));
  const [minutes, setMinutes] = useState();
  const [seconds, setSeconds] = useState();
  const [strokeNum, setStrokeNum] = useState(283);
  const inputRefs = useRef([]);
  const ref = useRef();
  useOnClickOutside(ref, () => {
    if (closeOnOutsideClick) {
      updateModalState(false);
    }
  });
  const FULL_DASH_ARRAY = 283;

  const resetDigits = () => {
    setDigits(Array(otpLength).fill(""));
  };

  const handleAfterResend = () => {
    ResendOTPSMSBayi(jwt)
      .then((result) => {
        setErrMsg("");
        setJwt(result.jwt);
        setDuration(
          Math.round((Date.parse(`${result.expiresAt}`) - Date.now()) / 1000)
        );
        setSuccessMsg(`${result}`);
        resetDigits();
      })
      .catch((e) => {
        setErrMsg(e.message);
        resetDigits();
      });
  };

  useEffect(() => {
    if (shouldShow) {
      setDigits(Array(otpLength).fill(""));
    }
  }, [shouldShow, otpLength]);

  useEffect(() => {
    let interval;
    if (duration) {
      interval = setInterval(() => {
        setDuration(duration - 1);
        setSeconds(duration % 60);
        setMinutes(Math.floor(duration / 60));
        setCircleDasharray();
      }, 1000);
    }

    return () => clearInterval(interval);
    //eslint-disable-next-line
  }, [duration, setDuration, updateModalState, handleAfterResend]);
  useEffect(() => {
    if (duration === 0) {
      setDuration(0);
      setSeconds(0);
    }
    //eslint-disable-next-line
  }, [duration]);

  useEffect(() => {
    setCode(digits.join(""));
    //eslint-disable-next-line
  }, [digits]);

  const handleDigitChange = (index, value) => {
    const char = String(value || "").replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = char;
    setDigits(next);
    if (char && index < otpLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleDigitKeyDown = (index, event) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, otpLength);
    if (!pasted) {
      return;
    }
    const next = Array(otpLength).fill("");
    pasted.split("").forEach((char, index) => {
      next[index] = char;
    });
    setDigits(next);
    const focusIndex = Math.min(pasted.length, otpLength - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleAfterSubmit();
  };
  function calculateTimeFraction() {
    const rawTimeFraction = duration / 120;
    return rawTimeFraction - (1 / 120) * (1 - rawTimeFraction);
  }
  function setCircleDasharray() {
    const circleDasharray = `${(
      calculateTimeFraction() * FULL_DASH_ARRAY
    ).toFixed(0)} 283`;
    setStrokeNum(circleDasharray);
  }

  if (shouldShow) {
    return (
      <div
        className="fixed inset-0 z-[100] overflow-y-auto overscroll-contain"
        aria-labelledby="modal-title"
        aria-modal="true"
      >
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 transition-opacity bg-primary-black bg-opacity-90"
            aria-hidden="true"
          ></div>
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <div
            ref={ref}
            className="max-w-lg inline-block align-bottom bg-white px-4 pt-5 pb-16 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle {{ modalSize }} sm:w-full sm:pb-16 sm:pt-12 sm:px-12 outline-none"
          >
            <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
              <button
                onClick={() => {
                  updateModalState(false);
                  resetDigits();
                  setErrMsg("");
                  if (handleAfterClose) handleAfterClose();
                }}
                type="button"
                className="bg-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
              >
                <span className="sr-only">Close</span>
                <svg role="img" className="w-6 h-6 text-primary-black">
                  <use xlinkHref="/assets/img/necromancers.svg#cross" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="justify-center flex-auto pb-6 text-center">
                <div className="flex justify-center">
                  <div className="base-timer">
                    <svg
                      className="base-timer__svg"
                      viewBox="0 0 100 100"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g className="base-timer__circle">
                        <circle
                          className="base-timer__path-elapsed"
                          cx="50"
                          cy="50"
                          r="45"
                          shape-rendering="optimizeQuality"
                        ></circle>
                        <path
                          id="base-timer-path-remaining"
                          strokeDasharray={strokeNum}
                          className="base-timer__path-remaining"
                          d="
                  M 50, 50
                  m -45, 0
                  a 45,45 0 1,0 90,0
                  a 45,45 0 1,0 -90,0
                "
                        ></path>
                      </g>
                    </svg>
                    <span id="base-timer-label" className="base-timer__label">
                      {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
                    </span>
                  </div>
                </div>
                <h2 className="py-4 modal-title">
                  {title || "Cep Telefonu Doğrula"}
                </h2>
                <p className="modal-content-text">
                  {description ||
                    (otpLength === 6
                      ? "Size gelen tek kullanımlık altı haneli kodu giriniz."
                      : "Size gelen tek kullanımlık dört haneli kodu giriniz.")}
                </p>
                <div
                  className={classNames(
                    "flex justify-center mt-8 form-input-wrapper",
                    otpLength > 4 ? "space-x-2 sm:space-x-4" : "space-x-6"
                  )}
                >
                  {digits.map((digit, index) => (
                    <input
                      key={`otp-${index}`}
                      ref={(el) => {
                        inputRefs.current[index] = el;
                      }}
                      value={digit}
                      onChange={(e) => handleDigitChange(index, e.target.value)}
                      onKeyDown={(e) => handleDigitKeyDown(index, e)}
                      onPaste={handlePaste}
                      type="text"
                      inputMode="numeric"
                      autoComplete={index === 0 ? "one-time-code" : "off"}
                      name={`otp-${index + 1}`}
                      className="form-input otp"
                      maxLength="1"
                      aria-label={`SMS kodu ${index + 1}. hane`}
                    />
                  ))}
                </div>
              </div>

              <div className="flex justify-center w-full mt-6">
                {!isLoading && (
                  <button
                    onClick={handleSubmit}
                    className="w-full max-w-full uppercase form-submit-btn"
                    disabled={
                      digits.join("").length !== otpLength || duration === 0
                    }
                  >
                    Doğrula
                  </button>
                )}
                {isLoading && (
                  <button
                    className="w-full max-w-full uppercase form-submit-btn opacity-50"
                    disabled
                  >
                    Doğrulanıyor...
                  </button>
                )}
              </div>

              <div className="w-full space-y-6">
                {showResend && (
                  <h6 className="mt-16 text-xs text-center">
                    Doğrulama kodu gelmedi mi?
                  </h6>
                )}
                <p
                  role="alert"
                  className="mt-2 text-validation-error text-center"
                  id="email-error"
                >
                  {errMsg}
                </p>
                {showResend && (
                  <div className="flex justify-center">
                    <div
                      onClick={handleAfterResend}
                      className="w-full max-w-full uppercase form-submit-btn revert hover:cursor-pointer"
                    >
                      Tekrar Gönder
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return null;
}

export default SmsVerification;
